import { Color, Spacing } from '@persona-id/design-tokens';
import { noop } from 'lodash/fp';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import BlockInputsConfig from 'dashboard/components/WorkflowControlPanel/BlockInputsConfig';
import OutputConfig from 'dashboard/components/WorkflowControlPanel/OutputConfig';
import StepConfig from 'dashboard/components/WorkflowControlPanel/StepConfig';
import StepConfigNew from 'dashboard/components/WorkflowControlPanel/StepConfigNew';
import TriggerConfigAccount from 'dashboard/components/WorkflowControlPanel/TriggerConfigAccount';
import TriggerConfigApi from 'dashboard/components/WorkflowControlPanel/TriggerConfigApi';
import TriggerConfigCase from 'dashboard/components/WorkflowControlPanel/TriggerConfigCase';
import TriggerConfigComputedField from 'dashboard/components/WorkflowControlPanel/TriggerConfigComputedField';
import TriggerConfigDirect from 'dashboard/components/WorkflowControlPanel/TriggerConfigDirect';
import TriggerConfigEvent from 'dashboard/components/WorkflowControlPanel/TriggerConfigEvent';
import TriggerConfigRecurring from 'dashboard/components/WorkflowControlPanel/TriggerConfigRecurring';
import TriggerConfigSchedule from 'dashboard/components/WorkflowControlPanel/TriggerConfigSchedule';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { TransactionType } from 'dashboard/models/transaction-type';
import {
  ALL_TRIGGER_TYPES,
  BaseTrigger,
  Trigger,
  TriggerAccount,
  TriggerApi,
  TriggerCase,
  TriggerComputedField,
  TriggerEvent,
  TriggerRecurring,
  TriggerSchedule,
} from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { TriggerObject, WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunSimulated } from 'dashboard/models/workflow-run-simulated';
import { WorkflowRunStep, WorkflowRunStepKnownObjects } from 'dashboard/models/workflow-step';
import {
  ActualWorkflowStepType,
  FakeWorkflowStepType,
  WorkflowStep,
  WorkflowStepAll,
  WorkflowStepInput,
  WorkflowStepType,
  WorkflowVersionOutput,
} from 'dashboard/models/workflow-version';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';
import { TokensToNode } from 'lib/workflows';

const ControlPanelContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  width: 400px;
  flex: none;
  padding: ${Spacing[3]};
  border-left: 1px solid ${Color.BorderHairline};

  // TODO: Fix overflow in control panel and bug with conditional steps panel not showing
  overflow-y: auto;
  overflow-x: hidden;
`;

const PanelContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  padding: ${Spacing[3]};
`;

interface WorkflowControlPanelProps {
  usableObjects: UsableObjectConfig[];
  stepsConfig?: WorkflowStep[];
  inputStep: WorkflowStepInput;
  workflowSchema: WorkflowSchema;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  workflowName: string;
  workflowId: string;
  editable: boolean;
  showFullWorkflowTree?: boolean;
  showRunStats?: boolean;
  activeNode: WorkflowStepAll | null;
  tokensToSteps: TokensToNode;
  validationErrors: Array<WorkflowError>;
  conditionalRouteToEdit?: number;
  setFocusedNodeToken: (nodeToken: string) => void;
  updateStep: (step: WorkflowStep, skipContext?: boolean, template?: TemplateFilter) => void;
  deleteStep: (stepToDelete: WorkflowStep) => void;
  pasteCopiedSteps?: (currStep: WorkflowStep) => void;
  updateInputStep: (inputStep: WorkflowStepInput, template?: TemplateFilter) => void;
  closePanel: () => void;
  setConditionalRouteToEdit: (routeNumber: number) => void;
  // Only for when loading workflow run page
  workflowRunStep?: WorkflowRunStep | null;
  knownObjects?: WorkflowRunStepKnownObjects[];
  triggerObject?: TriggerObject | null;
  workflowVersionOutput?: WorkflowVersionOutput[];
  updateWorkflowVersionOutput: (body: WorkflowVersionOutput[]) => void;
  workflowVersionExpiration?: number;
  updateWorkflowVersionExpiration?: (expiration: number) => void;
  workflowRun?: WorkflowRun | WorkflowRunSimulated;
  updateCycleIndex?: (idx: number, token: string) => void;
  isDirty?: boolean;
  isCurrentPublishedVersion?: boolean;
  isLoadingLoopCycle?: boolean;
  loopTokensToCycleIdx?: Record<string, number>;
  transactionTypes?: TransactionType[];
  disabledStepTypes?: WorkflowStepType[];
  canAddModule?: boolean;
  stepTypeTooltipTexts?: Partial<Record<WorkflowStepType, string>>;
  loadFilterFieldConfigs?: () => Promise<void>;
}

const WorkflowControlPanel: FC<WorkflowControlPanelProps> = ({
  usableObjects,
  stepsConfig,
  inputStep,
  workflowSchema,
  platformObjectJsonSchema,
  workflowName,
  editable,
  showFullWorkflowTree,
  showRunStats = false,
  activeNode,
  tokensToSteps,
  conditionalRouteToEdit,
  setFocusedNodeToken,
  updateStep,
  deleteStep,
  pasteCopiedSteps = noop,
  updateInputStep,
  closePanel,
  validationErrors,
  setConditionalRouteToEdit,
  workflowRunStep = null,
  knownObjects = null,
  triggerObject = null,
  workflowVersionOutput,
  updateWorkflowVersionOutput,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
  workflowRun,
  workflowId,
  updateCycleIndex,
  isDirty,
  isCurrentPublishedVersion,
  isLoadingLoopCycle = false,
  loopTokensToCycleIdx,
  transactionTypes,
  disabledStepTypes,
  canAddModule = false,
  stepTypeTooltipTexts,
  loadFilterFieldConfigs,
}) => {
  const errors = useMemo(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const token = ALL_TRIGGER_TYPES.some((triggerType) => triggerType === activeNode.type)
      ? BaseTrigger
      : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        activeNode.token;
    return validationErrors.filter((validationError) => validationError.token === token);
  }, [activeNode, validationErrors]);
  const { trigger, inputs } = inputStep;
  const updateTrigger = (newTrigger: Trigger, template?: TemplateFilter) =>
    updateInputStep({ ...inputStep, trigger: newTrigger }, template);
  const isModulesEnabled = useFeatureFlag('global:enable-workflow-modules');

  const renderControlPanelContent = () => {
    if (!activeNode) {
      return;
    }

    switch (activeNode.type) {
      case WorkflowStepType.Input: {
        if (!trigger) {
          return (
            <BlockInputsConfig
              inputs={inputs}
              editable={editable}
              errors={errors}
              handleClose={closePanel}
              updateInputs={(newInputs) => updateInputStep({ ...inputStep, inputs: newInputs })}
            />
          );
        }

        switch (trigger.type) {
          case ApiModel.TriggerComputedField:
            return (
              <TriggerConfigComputedField
                trigger={trigger as TriggerComputedField}
                editable={editable}
                handleClose={closePanel}
                updateTrigger={updateTrigger}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                transactionTypes={transactionTypes}
                errors={errors}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowVersionExpiration={workflowVersionExpiration}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
              />
            );
          case ApiModel.TriggerEvent:
            return (
              <TriggerConfigEvent
                trigger={trigger as TriggerEvent}
                editable={editable}
                errors={errors}
                workflowSchema={workflowSchema}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                transactionTypes={transactionTypes}
                handleClose={closePanel}
                updateTrigger={updateTrigger}
                tokensToSteps={tokensToSteps}
                triggerObject={triggerObject}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowVersionExpiration={workflowVersionExpiration}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
              />
            );
          case ApiModel.TriggerApi:
            return (
              <TriggerConfigApi
                trigger={trigger as TriggerApi}
                editable={editable}
                errors={errors}
                handleClose={closePanel}
                workflowId={workflowId}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowRun={workflowRun}
                updateTrigger={updateTrigger}
                isDirty={isDirty}
                isCurrentPublishedVersion={isCurrentPublishedVersion}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowVersionExpiration={workflowVersionExpiration}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
              />
            );
          case ApiModel.TriggerSchedule:
            return (
              <TriggerConfigSchedule
                trigger={trigger as TriggerSchedule}
                editable={editable}
                errors={errors}
                handleClose={closePanel}
                updateTrigger={updateTrigger}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowVersionExpiration={workflowVersionExpiration}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
              />
            );
          case ApiModel.TriggerRecurring:
            return (
              <TriggerConfigRecurring
                trigger={trigger as TriggerRecurring}
                workflowSchema={workflowSchema}
                editable={editable}
                errors={errors}
                handleClose={closePanel}
                updateTrigger={updateTrigger}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowVersionExpiration={workflowVersionExpiration}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
                loadFilterFieldConfigs={loadFilterFieldConfigs}
              />
            );
          case ApiModel.TriggerCase:
            return (
              <TriggerConfigCase
                trigger={trigger as TriggerCase}
                workflowName={workflowName}
                editable={editable}
                errors={errors}
                handleClose={closePanel}
                updateTrigger={updateTrigger}
                triggerObject={triggerObject}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowVersionExpiration={workflowVersionExpiration}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
              />
            );
          case ApiModel.TriggerAccount:
            return (
              <TriggerConfigAccount
                trigger={trigger as TriggerAccount}
                triggerObject={triggerObject}
                workflowName={workflowName}
                editable={editable}
                errors={errors}
                handleClose={closePanel}
                updateTrigger={updateTrigger}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowVersionExpiration={workflowVersionExpiration}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
              />
            );
          case ApiModel.TriggerDirect:
            return <TriggerConfigDirect handleClose={closePanel} />;
          default:
            return assertNever(trigger);
        }
      }
      case WorkflowStepType.Output:
        return (
          <OutputConfig
            trigger={trigger}
            editable={editable}
            errors={errors}
            outputs={workflowVersionOutput || []}
            runOutput={workflowRun?.attributes?.['output'] || null}
            updateOutput={updateWorkflowVersionOutput}
            handleClose={closePanel}
            transactionTypes={transactionTypes || []}
          />
        );

      case WorkflowStepType.Placeholder: {
        const disallowedStepTypes: ActualWorkflowStepType[] = [];
        if (tokensToSteps[activeNode.token]?.parent?.step?.type === WorkflowStepType.Loop) {
          disallowedStepTypes.push(WorkflowStepType.Loop);
        }
        if (!canAddModule || !isModulesEnabled) {
          disallowedStepTypes.push(WorkflowStepType.Module);
        }
        return (
          <StepConfigNew
            activeStep={activeNode}
            editable={editable}
            errors={errors}
            showFullWorkflowTree={showFullWorkflowTree}
            isInquiryWorkflow={trigger?.type === ApiModel.TriggerDirect}
            disallowedStepTypes={disallowedStepTypes}
            deleteStep={deleteStep}
            updateStep={updateStep}
            pasteCopiedSteps={pasteCopiedSteps}
            disabledStepTypes={disabledStepTypes || []}
            stepTypeTooltipTexts={stepTypeTooltipTexts || {}}
          />
        );
      }
      case WorkflowStepType.Action:
      case WorkflowStepType.Conditional:
      case WorkflowStepType.Loop:
      case WorkflowStepType.Module:
      case WorkflowStepType.Parallel:
      case WorkflowStepType.Wait:
      case FakeWorkflowStepType.MapOutput:
        return (
          <StepConfig
            usableObjects={usableObjects}
            inputStep={inputStep}
            workflowSchema={workflowSchema}
            platformObjectJsonSchema={platformObjectJsonSchema}
            editable={editable}
            errors={errors}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            stepsConfig={stepsConfig}
            tokensToSteps={tokensToSteps}
            activeStep={activeNode}
            handleClose={closePanel}
            updateStep={updateStep}
            deleteStep={deleteStep}
            focusStep={setFocusedNodeToken}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            conditionalRouteToEdit={conditionalRouteToEdit}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setConditionalRouteToEdit={setConditionalRouteToEdit}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            workflowRunStep={workflowRunStep}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            knownObjects={knownObjects}
            isLoadingLoopCycle={isLoadingLoopCycle}
            updateCycleIndex={updateCycleIndex}
            loopTokensToCycleIdx={loopTokensToCycleIdx}
            showRunStats={showRunStats}
          />
        );
      default:
        assertNever(activeNode);
    }
  };
  return (
    <ControlPanelContainer
      aria-label='Step configuration panel'
      role='dialog'
      id='workflow-control-panel__container'
    >
      <PanelContainer>{renderControlPanelContent()}</PanelContainer>
    </ControlPanelContainer>
  );
};

export default WorkflowControlPanel;
